/**
 * The page's one action, declared where the action lives — audit v5b D-07.
 *
 * The mockup puts a screen's primary action at the inline end of its header ("Approve 3
 * eligible", "Commit buy"), disabled with its reason when it cannot be taken. In this product
 * the header is rendered by the ROUTER (`src/app/routes/router.tsx`) and the action lives
 * deep inside the screen — inside a panel, often inside an `AsyncRegion`'s render prop, next
 * to the selection and the mutation it depends on. Lifting that state to the router would
 * move each chapter's decision logic out of the chapter.
 *
 * So the header keeps a SLOT and the screen PORTALS into it. `<PageAction>` renders its
 * children into the header's node; React portals keep the component's own context and state,
 * so the button is still the panel's button, wired to the panel's mutation — it is only drawn
 * somewhere else. It is MOVED, not copied: two buttons with one name would read to a screen
 * reader as two actions. Outside a routed page (a test, a story) there is no slot and the
 * children render where they are written, so the action is never lost.
 *
 * One action per page. A second `<PageAction>` would render beside the first; the rule is
 * the screen's to keep, and the header's layout does not pretend otherwise.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

const PageActionSlot = createContext<HTMLElement | null>(null);

/** Provided by the router, with the header's action node. */
export const PageActionSlotProvider = PageActionSlot.Provider;

export function PageAction({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const slot = useContext(PageActionSlot);
  return slot === null ? <>{children}</> : createPortal(children, slot);
}
